import { useCallback, useEffect, useState } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useParams } from 'react-router-dom';

import Loading from '@components/Loading';
import NotFound from '@components/NotFound';

import { useUser } from '@contexts/UserContext';

import { Repository } from '@models/Repository';
import { UserRouteParams } from '@models/RouteParams';

import { getUserRepositories } from '@services/RepositoryService';

import { handleRequestError } from '@helpers/handleRequestError';

import Item from '../RepositoryItem';
import { Container, ListCount, List } from './styles';

const Repositories: React.FC = () => {
  const { username } = useParams<UserRouteParams>();
  const { user } = useUser();
  const [repositories, setRepositories] = useState<Repository[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);

  const fetchRepositories = useCallback(
    async (currentPage?: number) => {
      try {
        if (!user) return;

        setLoading(true);

        const response = await getUserRepositories(username, currentPage);

        setPage((prevState) => prevState + 1);
        setRepositories((prevState) => [...prevState, ...response]);
      } catch (error) {
        handleRequestError('User repositories not found');
      } finally {
        setLoading(false);
      }
    },
    [user, username]
  );

  useEffect(() => {
    fetchRepositories();
  }, [fetchRepositories]);

  if (loading && repositories?.length === 0) {
    return (
      <Loading style={{ height: repositories?.length === 0 && '100vh' }} />
    );
  }

  if (!loading && repositories?.length === 0) {
    return (
      <NotFound>
        <p>Repositories is empty.</p>
      </NotFound>
    );
  }

  return (
    <Container id="repositories">
      <ListCount data-cy="list-count">
        {repositories?.length} of {user?.public_repos}
      </ListCount>

      <InfiniteScroll
        dataLength={repositories?.length ?? 30}
        next={() => fetchRepositories(page)}
        hasMore={user?.public_repos > repositories.length}
        loader={<Loading />}
        scrollableTarget={window.innerWidth > 950 && 'repositories'}
      >
        <List data-cy="repositories-list">
          {repositories?.map((repository) => (
            <Item key={repository.id} repository={repository} />
          ))}
        </List>
      </InfiniteScroll>
    </Container>
  );
};

export default Repositories;
